import cx from 'classnames';
import {
  Briefcase,
  Building2,
  ChartLine,
  ClipboardList,
  ClipboardPaste,
  LayoutGrid,
  Siren,
  Tags,
  Users,
} from 'lucide-react';
import {
  chaptersPath,
  clientsPath,
  clientsReportingPath,
  insightsOverviewPath,
  incidentsPath,
  insightsPath,
  membersPath,
  membersReportingListPath,
  organizationsPath,
  productsPath,
  productsReportingPath,
  sprintReportingPath,
} from '@app/constants/url';
import { Roles } from '@app/utils/types';
import { UserEntity } from '@data';
import { useOrganizations } from '@data/hooks/organizations';
import { useUnreportedDayCount } from '@data/hooks/reports';
import { useUnreadCommentCount } from '@data/hooks/sprintProducts';
import { useStore } from '@data/storages';
import React from 'react';
import { getOrganizationIdFromPathname, useOrganizationId, useReportingWindow } from '@app/utils/hooks';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@ui/components/ui/sidebar';

/** Trailing slash–agnostic compare (pathname is search/hash-free). */
function normalizePath(p: string): string {
  if (p.length <= 1) return p;
  return p.replace(/\/$/, '') || '/';
}

// When `end` is true, only exact path match — same as NavLink `end`.
function isSidebarItemActive(currentPath: string, to: string, end = false): boolean {
  const p = normalizePath(currentPath);
  const t = normalizePath(to);
  if (!t) return false;
  if (end) return p === t;
  return p === t || p.startsWith(`${t}/`);
}

function SidebarNavItem({
  to,
  icon: Icon,
  label,
  end = false,
  badge,
  matchPaths,
}: {
  to: string;
  icon: React.ElementType;
  label: string;
  end?: boolean;
  // Hidden at zero: an item permanently wearing a "0" trains people to ignore the badge entirely.
  badge?: number;
  // Extra paths that also light this item up. A merged section stays active on its peer route
  // (Members is active on both `/members-reporting` and `/members`).
  matchPaths?: string[];
}): JSX.Element {
  const { pathname } = useLocation();
  const active =
    isSidebarItemActive(pathname, to, end) || (matchPaths ?? []).some(p => isSidebarItemActive(pathname, p));
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        isActive={active}
        // The cva base hardcodes the active highlight as grey via `data-active:bg-sidebar-accent` /
        // `data-active:text-sidebar-accent-foreground`. Those match a plain `bg-*` on specificity but are
        // emitted later, so we override at the same `data-active:`/`active:`/hover level and tailwind-merge
        // drops the grey, leaving the brand orange (AC4). (Backgrounds only paint on these <Link> anchors at
        // all because normalize.css is now layered into `base` — see globals.css.)
        className={
          active
            ? 'bg-sidebar-primary font-medium text-sidebar-primary-foreground data-active:bg-sidebar-primary data-active:text-sidebar-primary-foreground active:bg-sidebar-primary active:text-sidebar-primary-foreground hover:bg-sidebar-primary hover:text-sidebar-primary-foreground'
            : undefined
        }
        render={
          <Link to={to} aria-current={active ? 'page' : undefined}>
            <Icon aria-hidden />
            <span>{label}</span>
            {!!badge && (
              // Turns over with the item instead of staying one flat colour: `destructive` sits a few
              // degrees of hue from the brand orange, so on the selected row it blended into its own
              // highlight. Selected reads as the row inverted, sharing `sidebar-primary` with the
              // background behind it so the two cannot drift if the brand orange is ever retuned.
              <span
                data-testid="sidebar-nav-badge"
                className={cx(
                  'ml-auto flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 text-xs font-semibold leading-none',
                  active ? 'bg-white text-sidebar-primary' : 'bg-destructive text-white',
                )}
              >
                {badge}
              </span>
            )}
          </Link>
        }
      />
    </SidebarMenuItem>
  );
}

export const NavItems = ({ user }: { user: UserEntity }): JSX.Element | null => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const orgIdFromRoute = useOrganizationId();
  const { organization } = useStore();
  const { data: orgs } = useOrganizations(user.id);
  /** URL wins so `to` + active state match the page the user is on (avoids zustand/first-org drift). */
  const resolvedOrgId =
    getOrganizationIdFromPathname(pathname) ?? orgIdFromRoute ?? organization?.id ?? orgs?.items?.[0]?.id ?? '';

  // The same window the To report tab lists, so the badge and the list cannot disagree.
  const unreportedWindow = useReportingWindow();
  const { data: unreported } = useUnreportedDayCount(resolvedOrgId || undefined, user.id, unreportedWindow);

  const role = orgs?.items.find(org => org.id === resolvedOrgId)?.role;

  const isPMorAdmin = role === Roles.admin || role === Roles.productManager;
  const isAdmin = role === Roles.admin;

  // Asked for only by the people who can open the section it counts; the endpoint refuses anybody else.
  // Both this and the role above sit before the early return, because hooks cannot run conditionally.
  const { data: unreadComments } = useUnreadCommentCount(isPMorAdmin ? resolvedOrgId : undefined);

  if (!resolvedOrgId) {
    return null;
  }

  const orgPath = (build: (id: string) => string) => (resolvedOrgId ? build(resolvedOrgId) : organizationsPath);

  return (
    <SidebarMenu className="gap-1">
      {role === Roles.client ? (
        <SidebarNavItem to={orgPath(clientsReportingPath)} icon={ClipboardPaste} label={t('titles.reports')} />
      ) : (
        <>
          {/* The team's reporting is a Timesheets view, so this item stays lit on its route too. */}
          <SidebarNavItem
            to={orgPath(sprintReportingPath)}
            end
            matchPaths={[orgPath(membersReportingListPath)]}
            icon={Briefcase}
            label={t('sidebar.sprintReporting')}
            badge={unreported?.count}
          />
          {isPMorAdmin && (
            <>
              {/* Counts reports with an unread comment; this list is the only place to answer them. */}
              <SidebarNavItem
                to={orgPath(productsReportingPath)}
                icon={ClipboardList}
                label={t('sidebar.reporting')}
                badge={unreadComments?.count}
              />
              <SidebarNavItem to={orgPath(productsPath)} icon={LayoutGrid} label={t('sidebar.products')} />
              <SidebarNavItem to={orgPath(membersPath)} icon={Users} label={t('sidebar.members')} />
              <SidebarNavItem to={orgPath(clientsPath)} icon={Building2} label={t('sidebar.clients')} />
              {/* Its own section, not a tab inside a product: it has to be reachable before the product is known. */}
              <SidebarNavItem to={orgPath(incidentsPath)} icon={Siren} label={t('sidebar.incidents')} />
              {/* Lands on the Insights tab; matchPaths keeps the item active on Exports, its peer route. */}
              <SidebarNavItem
                to={orgPath(insightsOverviewPath)}
                matchPaths={[orgPath(insightsPath)]}
                icon={ChartLine}
                label={t('sidebar.insights')}
              />
            </>
          )}
          {isAdmin && <SidebarNavItem to={orgPath(chaptersPath)} icon={Tags} label={t('sidebar.chapters')} />}
        </>
      )}
    </SidebarMenu>
  );
};
